import {
  DEFAULT_CONTRACT_VALUE_RANGE,
  DEFAULT_METRIC_SCORE_RANGE,
  METRIC_RANGE_KEYS,
  type FilterState,
} from "../constants/filterDefaults";
import { calculateActiveFilterCount } from "./filterUtils";

import type { ColumnData, Player } from "../types/types";

import { parseNumericValue } from "@/utils/currencyParsing";

/**
 * Normalized player shape the board filters operate on. Both the board-card
 * player and the sidebar (watchlist/roster) player are mapped to this before
 * filtering so the same predicate works for either source.
 */
export interface PlayerFilterData {
  id: string;
  archetype?: string;
  age?: number;
  gpr?: number;
  /** Player valuation midpoint in millions (matches the filter slider units). */
  contractValueM?: number;
  teamName?: string;
  contractMonths?: number | null;
  tags?: { id: string }[];
  /**
   * SE-6871: the player's PERFORMANCE / GOALKEEPER metric scores, joined from
   * the board-scoped metrics read. Absent while that read has not resolved —
   * the metric predicates then pass (the board's lenient-null convention),
   * and the drawer hides the two sections so an inert filter cannot be
   * committed in the first place.
   */
  metrics?: BoardPlayerMetrics;
}

/**
 * Per-player metric scores for the PERFORMANCE / GOALKEEPER filter sections
 * (SE-6871), keyed to match `METRIC_RANGE_KEYS` minus the `Range` suffix
 * semantics: each entry is the 0–100 categorical score the corresponding
 * range filters.
 */
export interface BoardPlayerMetrics {
  readonly carrying?: number | null;
  readonly dribbling?: number | null;
  readonly passing?: number | null;
  readonly shooting?: number | null;
  readonly gkPassing?: number | null;
  readonly gkShotStopping?: number | null;
  readonly gkPositioning?: number | null;
  /* SE-7144: FIT & RATINGS parity — same sidecar row, same DB scale. */
  readonly fitScore?: number | null;
  readonly teamStyleFit?: number | null;
}

/** Metric range key → the metrics field its predicate reads. */
const METRIC_FIELD_BY_RANGE_KEY = {
  carryingRange: "carrying",
  dribblingRange: "dribbling",
  passingRange: "passing",
  shootingRange: "shooting",
  gkPassingRange: "gkPassing",
  gkShotStoppingRange: "gkShotStopping",
  gkPositioningRange: "gkPositioning",
} as const satisfies Record<
  (typeof METRIC_RANGE_KEYS)[number],
  keyof BoardPlayerMetrics
>;

/** Active GPV bounds destined for the server, in absolute euros. */
export interface ServerGpvRange {
  /** Inclusive lower GPV midpoint bound in euros */
  min: number;
  /** Inclusive upper GPV midpoint bound in euros */
  max: number;
}

/**
 * Resolve the board's contract-value filter into server-side GPV midpoint
 * bounds for `listWatchlistPlayers` (SE-6496).
 *
 * The slider operates in millions; the server args are absolute euros. A range
 * spanning the full slider span (the dynamic bounds when known, else the
 * unbounded defaults — mirroring `calculateActiveFilterCount`) is inactive and
 * resolves to null so no bound is sent: the server excludes players without a
 * GPV whenever a bound is present, and an untouched filter must not hide them.
 * @param contractValueRange - Current [min, max] slider selection in millions
 * @param dynamicContractValueRange - Full slider span derived from board data
 * @returns Euro-denominated bounds when the filter is active, else null
 */
export const resolveServerGpvRange = (
  contractValueRange: [number, number],
  dynamicContractValueRange?: [number, number]
): ServerGpvRange | null => {
  const fullRange = dynamicContractValueRange ?? DEFAULT_CONTRACT_VALUE_RANGE;
  const isActive =
    contractValueRange[0] > fullRange[0] ||
    contractValueRange[1] < fullRange[1];
  if (!isActive) return null;
  return {
    min: contractValueRange[0] * 1_000_000,
    max: contractValueRange[1] * 1_000_000,
  };
};

const matchesPositionFilter = (
  player: PlayerFilterData,
  positions: string[]
): boolean => {
  if (positions.length === 0) return true;
  return !!player.archetype && positions.includes(player.archetype);
};

const matchesAgeFilter = (
  player: PlayerFilterData,
  ageRange: [number, number]
): boolean => {
  const age = player.age;
  if (!age) return true;
  return age >= ageRange[0] && age <= ageRange[1];
};

const matchesGprFilter = (
  player: PlayerFilterData,
  gprRange: [number, number]
): boolean => {
  const gpr = player.gpr;
  if (gpr === undefined || gpr === null) return true;
  return gpr >= gprRange[0] && gpr <= gprRange[1];
};

const matchesContractValueFilter = (
  player: PlayerFilterData,
  contractValueRange: [number, number]
): boolean => {
  const value = player.contractValueM;
  if (value === undefined || value === null || Number.isNaN(value)) return true;
  return value >= contractValueRange[0] && value <= contractValueRange[1];
};

const matchesTeamFilter = (
  player: PlayerFilterData,
  teams: string[]
): boolean => {
  if (teams.length === 0) return true;
  return !!player.teamName && teams.includes(player.teamName);
};

/**
 * Derive a player's contract status bucket from months remaining.
 * @param contractMonths - Months remaining on the player's contract
 * @returns Contract status label
 */
export const getContractStatus = (contractMonths?: number | null): string => {
  if (contractMonths == null) return "Unknown";
  if (contractMonths === 0) return "Free Agent";
  if (contractMonths <= 6) return "Expiring Soon";
  return "Active Contract";
};

const matchesContractStatusFilter = (
  player: PlayerFilterData,
  contractStatuses: string[]
): boolean => {
  if (contractStatuses.length === 0) return true;
  return contractStatuses.includes(getContractStatus(player.contractMonths));
};

const matchesTagFilter = (
  player: PlayerFilterData,
  tags: string[]
): boolean => {
  if (!tags || tags.length === 0) return true;
  const playerTagIds = (player.tags || []).map(tag => tag.id);
  return tags.some(tagId => playerTagIds.includes(tagId));
};

/**
 * Whether a player passes every active PERFORMANCE / GOALKEEPER metric range
 * (SE-6871). Strict on unknowns, unlike the board's age/GPR/value predicates:
 * the Watchlist applies these same ranges in SQL (`"COL" >= :min` — NULL
 * fails), so a player with no score for a narrowed metric is excluded there,
 * and the ticket requires the two panels to behave identically.
 * @param player - Normalized player data.
 * @param filters - Current board filter state.
 * @returns True when every narrowed metric range admits the player.
 */
const matchesMetricFilters = (
  player: PlayerFilterData,
  filters: FilterState
): boolean =>
  METRIC_RANGE_KEYS.every(key => {
    const range = filters[key];
    const isActive =
      range[0] > DEFAULT_METRIC_SCORE_RANGE[0] ||
      range[1] < DEFAULT_METRIC_SCORE_RANGE[1];
    if (!isActive) return true;
    const value = player.metrics?.[METRIC_FIELD_BY_RANGE_KEY[key]];
    if (value === undefined || value === null) return false;
    return value >= range[0] && value <= range[1];
  });

/**
 * Whether a player passes all active board filters.
 * @param player - Normalized player data
 * @param filters - Current board filter state
 * @returns True when the player matches every active filter dimension
 */
export const shouldIncludePlayer = (
  player: PlayerFilterData,
  filters: FilterState
): boolean =>
  matchesPositionFilter(player, filters.positions) &&
  matchesAgeFilter(player, filters.ageRange) &&
  matchesGprFilter(player, filters.gprRange) &&
  matchesContractValueFilter(player, filters.contractValueRange) &&
  matchesTeamFilter(player, filters.teams) &&
  matchesContractStatusFilter(player, filters.contractStatus) &&
  matchesTagFilter(player, filters.tags) &&
  matchesMetricFilters(player, filters);

/**
 * Map a board-card player to the shape the filter predicates read.
 * @remarks
 * Every numeric field reads the SAME normalized board-card value the drawer's
 * dynamic slider bounds are computed from (`useBoardFilterOptions`): `gpr` is
 * the card's copy — also what the table's GPR badge displays — and
 * `contractValueM` parses the same display string the bounds parse. Reading
 * the raw `bioData` values instead (as the left rail's mapper does) skews the
 * predicate against the bounds: a player whose raw GPR sits above its
 * displayed rounding would vanish under a range whose visible maximum
 * includes them, and the identity fast-path (active-count 0 at full span)
 * would disagree with the predicates about what "inactive" means.
 *
 * Board cards DO carry their tags, so the tag predicate gets them (the rail's
 * players carry none and pass `[]`).
 * @param player - A column's board-card player
 * @returns Normalized filter data for the player
 */
const toBoardCardFilterData = (player: Player): PlayerFilterData => {
  const parsedContractValue = parseNumericValue(player.contractValue);
  return {
    id: player.id,
    archetype: player.archetype,
    age: player.age,
    gpr: player.gpr,
    contractValueM: Number.isFinite(parsedContractValue)
      ? parsedContractValue
      : undefined,
    teamName: player.teamName,
    contractMonths: player.contractMonths,
    tags: player.tags ?? [],
  };
};

/**
 * Apply the committed board filters to every column's players — the TABLE
 * renderer's dataset (SE-6871).
 * @remarks
 * The Kanban surface deliberately does NOT filter placed cards — board filters
 * scope its left add-players rail only (the PR #6047 ruling). The Table view
 * has no rail, so without this the drawer committed filters into state that
 * changed nothing the user could see. The table's rows ARE its page content,
 * exactly as the Watchlist's list is, so the committed filters narrow them
 * here — same predicates, same client-side path, applied to the table
 * derivation alone so the Kanban semantics stay untouched.
 *
 * Column identity is preserved (a fully filtered-out stage contributes no
 * rows rather than disappearing structurally), `count` stays truthful, and
 * the identity fast-path keeps referential stability when nothing is active —
 * all mirroring `applyBoardSearchAndSort`, which runs before this in the same
 * derivation.
 * @param columns - The table's column data, or undefined while loading
 * @param filters - The committed board filter state
 * @param dynamicGprRange - Full GPR span from board data, for activity detection
 * @param dynamicContractValueRange - Full value span from board data, for activity detection
 * @param metricsById - Per-player PERFORMANCE / GOALKEEPER scores from the board-scoped sidecar read (SE-6871)
 * @returns Columns with filtered players, or the input when nothing is active
 */
export function applyBoardFiltersToColumns<T extends ColumnData[] | undefined>(
  columns: T,
  filters: FilterState,
  dynamicGprRange?: [number, number],
  dynamicContractValueRange?: [number, number],
  metricsById?: ReadonlyMap<string, BoardPlayerMetrics>
): T {
  if (!columns) return columns;
  const activeCount = calculateActiveFilterCount(
    filters,
    dynamicGprRange,
    dynamicContractValueRange
  );
  if (activeCount === 0) return columns;

  return columns.map(column => {
    const players = column.players.filter(player =>
      shouldIncludePlayer(
        {
          ...toBoardCardFilterData(player),
          // SE-6871: the PERFORMANCE / GOALKEEPER scores live in a separate
          // board-scoped read, joined here by player id.
          metrics: metricsById?.get(player.id),
        },
        filters
      )
    );
    return { ...column, players, count: players.length };
  }) as T;
}
